import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Search,
  Users,
  Sparkles,
  Settings,
  LogOut,
  Zap,
  BarChart3,
  Megaphone,
  Flame,
  Send,
  Menu,
  X,
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard',  icon: LayoutDashboard, label: 'Pipeline',          short: 'Pipeline' },
  { path: '/search',     icon: Search,          label: 'Find Clients',      short: 'Find' },
  { path: '/leads',      icon: Users,           label: 'Potential Clients', short: 'Clients' },
  { path: '/outreach',   icon: Send,            label: 'Outreach',          short: 'Outreach' },
  { path: '/challenge',  icon: Flame,           label: '90-Day Challenge',  short: 'Challenge' },
  { path: '/marketing',  icon: Megaphone,       label: 'Marketing Studio',  short: 'Studio' },
  { path: '/ai-tools',   icon: Sparkles,        label: 'AI Tools',          short: 'AI' },
  { path: '/analytics',  icon: BarChart3,       label: 'Analytics',         short: 'Stats' },
  { path: '/settings',   icon: Settings,        label: 'Settings',          short: 'Settings' },
];

const MOBILE_BOTTOM_NAV = NAV_ITEMS.filter(n =>
  ['/dashboard', '/leads', '/outreach', '/challenge'].includes(n.path)
);

const Sidebar = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initial = (user?.full_name || user?.email || 'U').charAt(0).toUpperCase();

  const SidebarBody = (
    <>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-slate-100">
        <NavLink to="/dashboard" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center shadow-glow">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-[17px] text-slate-900 leading-none">Nexus<span className="gradient-text">Leads</span></h1>
            <p className="text-[10px] text-slate-500 font-medium tracking-[0.14em] uppercase mt-1">Client Acquisition</p>
          </div>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.path.replace('/', '')}`}
            onClick={() => setDrawerOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-violet-50 to-blue-50 text-violet-700 ring-1 ring-violet-100 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`flex w-7 h-7 items-center justify-center rounded-md transition-colors ${
                  isActive ? 'bg-gradient-to-br from-violet-600 to-blue-600 text-white' : 'text-slate-500 group-hover:text-slate-700'
                }`}>
                  <item.icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                </span>
                <span className="truncate">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="p-3 border-t border-slate-100">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.full_name || 'User'}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="mt-1 flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[260px] bg-white border-r border-slate-200 h-screen sticky top-0 z-30">
        {SidebarBody}
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <NavLink to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <span className="font-heading font-extrabold text-[15px]">Nexus<span className="gradient-text">Leads</span></span>
        </NavLink>
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="open-mobile-nav"
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-700"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col animate-slide-up">
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute right-3 top-3 p-2 rounded-lg hover:bg-slate-100 text-slate-700 z-10"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
            {SidebarBody}
          </aside>
        </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 grid grid-cols-4 h-16">
        {MOBILE_BOTTOM_NAV.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`mobile-nav-${item.path.replace('/', '')}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${
                isActive ? 'text-violet-700' : 'text-slate-500'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`flex w-9 h-9 items-center justify-center rounded-lg ${
                  isActive ? 'bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-glow' : ''
                }`}>
                  <item.icon className="w-4 h-4" strokeWidth={2.4} />
                </span>
                {item.short}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </>
  );
};

export default Sidebar;
